import {
    shouldRecomputeReimbTypes,
    anyReimbTypeRecomputeNeeded,
    roundReimbPsf,
} from './reimbTypesPersistence';

const cloneDeep = require('lodash.clonedeep');

// Marketing
export const marketingPriceIndex = 2;

// Pricing
export const analysisTableValueIndex = 1;
export const pricingCurrentPercentIndex = 1;
export const pricingCurrentValueIndex = 2;
export const pricingProformaPercentIndex = 5;
export const pricingProformaValueIndex = 6;

// Rent Roll
export const tenantIndex = 1;
export const annualRentIndex = 4;

// Reimbursement Types
export const reimbursementTypesNo = 15;
export const defaultReimbursementTypesNo = 7;
export const taxAssessmentsNo = 4;
export const adminFeeCellIndex = 2;
export const camCheckboxCellIndex = 3;
export const camCellIndex = 4;
export const mgmtCheckboxCellIndex = 5;
export const mgmtCellIndex = 6;
export const insuranceCheckboxCellIndex = 7;
export const insuranceCellIndex = 8;
export const taxesCheckboxCellIndex = 9;
export const taxesCellIndex = 10;
export const grossReimbCode = 7;
export const reimbCodeIndex = 2;
export const prorataShareIndex = 0;

// Reimbursable Expenses
export const reimbMgmtIndex = 5;
export const reimbInsuranceIndex = 6;
export const reimbTaxesIndex = 7;

// Expenses
export const camDefaultRowNo = 5;
export const expensesValueIndex = 2;
export const expensesPsfIndex = 3;

// ProForma Expenses
export const expensesPfPsfIndex = 6;

export function computeCamValue(checkbox, sum) {
    if (!checkbox)
        return 0;
    return roundReimbPsf(sum);
}

export function computeMgmtValue(checkbox, mgmt, cam, adminFee) {
    if (!checkbox)
        return 0;
    if (!adminFee)
        return roundReimbPsf(mgmt);
    return roundReimbPsf(cam * (adminFee / 100));
}

export function computeInsuranceValue(checkbox, insurance) {
    if (!checkbox)
        return 0;
    return roundReimbPsf(insurance);
}

export function computeTaxesValue(checkbox, taxes) {
    if (!checkbox)
        return 0;
    return roundReimbPsf(taxes);
}

export function recomputeCam({tab, tabs, precomputedValues, index, newCheckbox = null}) {
    const cells = tabs[tab].tables['reimbursement-types'].rows[index].valueCells;
    const checkbox = newCheckbox === null ? cells[camCheckboxCellIndex].value : newCheckbox;
    const totalCamIndex = tab === 'current-reimbursement-tab' ? expensesPsfIndex : expensesPfPsfIndex;
    let sum = tabs['expenses-tab'].tables['expenses'].rows['total-cam'].valueCells[totalCamIndex].getComputedValue(tabs, precomputedValues).internalValue || 0;
    if (!isFinite(sum))
        sum = 0;
    return computeCamValue(checkbox, sum);
}

export function recomputeMgmt({tab, tabs, precomputedValues, index, newCheckbox = null, newAdminFee = null, newCam = null}) {
    const cells = tabs[tab].tables['reimbursement-types'].rows[index].valueCells;
    const checkbox = newCheckbox === null ? cells[mgmtCheckboxCellIndex].value : newCheckbox;
    const cam = newCam === null ? cells[camCellIndex].value : newCam;
    const adminFee = newAdminFee === null ? cells[adminFeeCellIndex].value : newAdminFee;
    const rowIdentifier = 'management';
    let mgmt = tabs[tab].tables['reimbursable-op-ex'].rows[rowIdentifier].valueCells[reimbMgmtIndex].getComputedValue(tabs, rowIdentifier, precomputedValues).internalValue || 0;
    if (!isFinite(mgmt))
        mgmt = 0;
    return computeMgmtValue(checkbox, mgmt, cam, adminFee);
}

export function recomputeInsurance({tab, tabs, precomputedValues, index, newCheckbox = null}) {
    const cells = tabs[tab].tables['reimbursement-types'].rows[index].valueCells;
    const checkbox = newCheckbox === null ? cells[insuranceCheckboxCellIndex].value : newCheckbox;
    const rowIdentifier = 'insurance';
    let insurance = tabs[tab].tables['reimbursable-op-ex'].rows[rowIdentifier].valueCells[reimbInsuranceIndex].getComputedValue(tabs, rowIdentifier, precomputedValues).internalValue || 0;
    if (!isFinite(insurance))
        insurance = 0;
    return computeInsuranceValue(checkbox, insurance);
}

export function recomputeTaxes({tab, tabs, precomputedValues, index, newCheckbox = null}) {
    const cells = tabs[tab].tables['reimbursement-types'].rows[index].valueCells;
    const checkbox = newCheckbox === null ? cells[taxesCheckboxCellIndex].value : newCheckbox;
    const rowIdentifier = 'real-estate-taxes';
    let taxes = tabs[tab].tables['reimbursable-op-ex'].rows[rowIdentifier].valueCells[reimbTaxesIndex].getComputedValue(tabs, rowIdentifier, precomputedValues).internalValue || 0;
    if (!isFinite(taxes))
        taxes = 0;
    return computeTaxesValue(checkbox, taxes);
}

export function computeReimbTypes({result, tab, precomputedValues, newSqft = null, newCam = null, newMgmt = null, newInsurance = null, newTaxes = null, reimbTypesManuallyModified}) {
    if (reimbTypesManuallyModified)
        return {
            data: {
                tabs: result,
            }
        };

    // Flags are booleans that default to false. Only recompute when explicitly true
    // (expense/sqft actually changed). Checking !== null incorrectly treated false as
    // "dirty" and overwrote saved CAM/mgmt/insurance/tax PSF values on every tab mount
    // and Submit — which made Total Exp. Reimb. appear to revert after save.
    const {
        cam: shouldRecomputeCam,
        mgmt: shouldRecomputeMgmt,
        insurance: shouldRecomputeInsurance,
        taxes: shouldRecomputeTaxes,
    } = shouldRecomputeReimbTypes({ newCam, newSqft, newMgmt, newInsurance, newTaxes });

    if (!anyReimbTypeRecomputeNeeded({
        cam: shouldRecomputeCam,
        mgmt: shouldRecomputeMgmt,
        insurance: shouldRecomputeInsurance,
        taxes: shouldRecomputeTaxes,
    })) {
        return {
            data: {
                tabs: result,
            }
        };
    }

    const tabs = cloneDeep(result);
    const state = {};
    if (shouldRecomputeCam) {
        for (let index = 0; index < defaultReimbursementTypesNo; index++) {
            if (
                tabs &&
                tabs[tab] &&
                tabs[tab].tables &&
                tabs[tab].tables['reimbursement-types'] &&
                tabs[tab].tables['reimbursement-types'].rows &&
                tabs[tab].tables['reimbursement-types'].rows[index] &&
                tabs[tab].tables['reimbursement-types'].rows[index].valueCells &&
                tabs[tab].tables['reimbursement-types'].rows[index].valueCells[camCellIndex]
              ) {
                tabs[tab].tables['reimbursement-types'].rows[index].valueCells[camCellIndex].value = recomputeCam({
                  tab,
                  tabs,
                  precomputedValues,
                  index,
                });
              } 
              
        }
        if (newCam)
            state.camPSFSumUpdated = false;
        if (newSqft)
            state.sqFtUpdated = false;
    }
    if (shouldRecomputeMgmt) {
        for (let index = 0; index < defaultReimbursementTypesNo; index++) {

            if (
                tabs &&
                tabs[tab] &&
                tabs[tab].tables &&
                tabs[tab].tables['reimbursement-types'] &&
                tabs[tab].tables['reimbursement-types'].rows &&
                tabs[tab].tables['reimbursement-types'].rows[index] &&
                tabs[tab].tables['reimbursement-types'].rows[index].valueCells &&
                tabs[tab].tables['reimbursement-types'].rows[index].valueCells[mgmtCellIndex]
              ) {
                tabs[tab].tables['reimbursement-types'].rows[index].valueCells[mgmtCellIndex].value = recomputeMgmt({
                    tab,
                    tabs,
                    precomputedValues,
                    index,
                });
              } 
           
        }
        if (newMgmt)
            state.mgmtUpdated = false;
        if (newSqft)
            state.sqFtUpdated = false;
    }
    if (shouldRecomputeInsurance) {
        for (let index = 0; index < defaultReimbursementTypesNo; index++) {

            if (
                tabs &&
                tabs[tab] &&
                tabs[tab].tables &&
                tabs[tab].tables['reimbursement-types'] &&
                tabs[tab].tables['reimbursement-types'].rows &&
                tabs[tab].tables['reimbursement-types'].rows[index] &&
                tabs[tab].tables['reimbursement-types'].rows[index].valueCells &&
                tabs[tab].tables['reimbursement-types'].rows[index].valueCells[insuranceCellIndex]
              ) {
                tabs[tab].tables['reimbursement-types'].rows[index].valueCells[insuranceCellIndex].value = recomputeInsurance({
                    tab,
                    tabs,
                    precomputedValues,
                    index,
                });
              } 
           
        }
        if (newInsurance)
            state.insuranceUpdated = false;
        if (newSqft)
            state.sqFtUpdated = false;
    }
    if (shouldRecomputeTaxes) {
        for (let index = 0; index < defaultReimbursementTypesNo; index++) {

            if (
                tabs &&
                tabs[tab] &&
                tabs[tab].tables &&
                tabs[tab].tables['reimbursement-types'] &&
                tabs[tab].tables['reimbursement-types'].rows &&
                tabs[tab].tables['reimbursement-types'].rows[index] &&
                tabs[tab].tables['reimbursement-types'].rows[index].valueCells &&
                tabs[tab].tables['reimbursement-types'].rows[index].valueCells[taxesCellIndex]
              ) {
                tabs[tab].tables['reimbursement-types'].rows[index].valueCells[taxesCellIndex].value = recomputeTaxes({
                    tab,
                    tabs,
                    precomputedValues,
                    index,
                });
              } 
          
        }
        if (newTaxes)
            state.taxesUpdated = false;
        if (newSqft)
            state.sqFtUpdated = false;
    }

    state.data = {tabs};
    return state;
}